import React, { useState } from "react";
import { toast } from "react-toastify";

import { PlatformSubscriptionRow } from "../../../common/platformTypes";
import { SubscriptionActionsToolbar } from "../../../components/SubscriptionActionsToolbar";
import {
  subscriptionSetActive,
  subscriptionTransferOwner,
  subscriptionUpdate,
} from "../../../services/api/Platform";
import { ConfirmWithMemoModal } from "../../Subscription/components/ConfirmWithMemoModal";
import { TransferSubscriptionModal } from "../../Subscription/components/TransferSubscriptionModal";
import { PlatformSubscriptionDetails } from "../components/PlatformSubscriptionDetails";
import { UpdatePlatformSubscriptionModal } from "../components/UpdatePlatformSubscriptionModal";

type Props = {
  subscription: PlatformSubscriptionRow;
  reload: () => Promise<void>;
};

export const SubscriptionTab: React.FunctionComponent<Props> = ({
  subscription,
  reload,
}) => {
  const [updateOpen, setUpdateOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);

  // Gate on the stored flag, not `entitled` — an expired-but-active row still
  // needs Deactivate, and offering Reactivate on it would be a no-op.
  const active = subscription.active !== false;

  /**
   * Runs a modal's save and closes it only once that has landed. A refusal is
   * re-thrown so the modal can show it with the operator's typing still in
   * place — closing first turned every refusal into "start again".
   */
  async function save(
    action: () => Promise<unknown>,
    success: string,
    close: () => void,
  ) {
    await action();
    close();
    toast.success(success);
    try {
      await reload();
    } catch (e) {
      // The save landed; only the refresh failed, and saying so beats leaving
      // the page showing what it showed before.
      toast.error(
        `Saved, but the page could not be refreshed: ${(e as Error).message}`,
      );
    }
  }

  return (
    <>
      <SubscriptionActionsToolbar
        onUpdateSubscriptionClick={() => setUpdateOpen(true)}
        onTransferOwnershipClick={
          active ? () => setTransferOpen(true) : undefined
        }
        onDeactivateClick={active ? () => setDeactivateOpen(true) : undefined}
        onReactivateClick={active ? undefined : () => setReactivateOpen(true)}
      />

      {updateOpen && (
        <UpdatePlatformSubscriptionModal
          isOpen={updateOpen}
          subscription={subscription}
          onClose={() => setUpdateOpen(false)}
          onSubmit={(params) =>
            save(
              () =>
                subscriptionUpdate({
                  subscriptionId: subscription._id,
                  ...params,
                }),
              "Subscription updated",
              () => setUpdateOpen(false),
            )
          }
        />
      )}

      <TransferSubscriptionModal
        isOpen={transferOpen}
        placeholder="platform user id of the new owner, e.g. usr-dev:…"
        onClose={() => setTransferOpen(false)}
        onSubmit={({ userId }) =>
          save(
            () =>
              subscriptionTransferOwner({
                subscriptionId: subscription._id,
                userId,
              }),
            "Transferred ownership",
            () => setTransferOpen(false),
          )
        }
      />

      <ConfirmWithMemoModal
        isOpen={deactivateOpen}
        title="Deactivate Subscription"
        onClose={() => setDeactivateOpen(false)}
        onSubmit={({ memo }) =>
          save(
            () =>
              subscriptionSetActive({
                subscriptionId: subscription._id,
                active: false,
                memo,
              }),
            "Deactivated subscription",
            () => setDeactivateOpen(false),
          )
        }
      />

      <ConfirmWithMemoModal
        isOpen={reactivateOpen}
        title="Reactivate Subscription"
        onClose={() => setReactivateOpen(false)}
        onSubmit={({ memo }) =>
          save(
            () =>
              subscriptionSetActive({
                subscriptionId: subscription._id,
                active: true,
                memo,
              }),
            "Reactivated subscription",
            () => setReactivateOpen(false),
          )
        }
      />

      <PlatformSubscriptionDetails subscription={subscription} />
    </>
  );
};
